import clsxm from '@/lib/clsxm';

// The showcase video for the voices that act on audio tags. Shared by the
// /tts What's new card and the text-to-speech landing pages.
export const TTS_SHOWCASE_VIDEO_ID = 'QXnphKfFTTo';
export const TTS_SHOWCASE_VIDEO_LENGTH = '5:49';
export const TTS_SHOWCASE_VIDEO_URL = `https://www.youtube.com/watch?v=${TTS_SHOWCASE_VIDEO_ID}`;

export function PlayGlyph({ className }: { className?: string }) {
  return (
    <svg
      viewBox='0 0 24 24'
      fill='currentColor'
      className={className}
      aria-hidden='true'
    >
      <path d='M7 4.5v15a1 1 0 0 0 1.52.85l12-7.5a1 1 0 0 0 0-1.7l-12-7.5A1 1 0 0 0 7 4.5Z' />
    </svg>
  );
}

export function PauseGlyph({ className }: { className?: string }) {
  return (
    <svg
      viewBox='0 0 24 24'
      fill='currentColor'
      className={className}
      aria-hidden='true'
    >
      <rect x='6' y='4.5' width='4' height='15' rx='1.2' />
      <rect x='14' y='4.5' width='4' height='15' rx='1.2' />
    </svg>
  );
}

// A YouTube poster that becomes the real player only when clicked, so the
// page never loads YouTube's scripts for people who don't watch. The parent
// owns `playing` (closing a dialog should be able to stop the video).
export default function YouTubeFacade({
  videoId,
  title,
  length,
  playing,
  onPlay,
  playLabel,
  className,
}: {
  videoId: string;
  title: string;
  length?: string;
  playing: boolean;
  onPlay: () => void;
  playLabel: string;
  className?: string;
}) {
  return (
    <div
      className={clsxm(
        'relative aspect-video w-full overflow-hidden bg-[oklch(0.14_0.02_278)]',
        className,
      )}
    >
      {playing ? (
        <iframe
          className='absolute inset-0 h-full w-full'
          src={`https://www.youtube-nocookie.com/embed/${videoId}?autoplay=1&rel=0&modestbranding=1&playsinline=1`}
          title={title}
          allow='autoplay; encrypted-media; picture-in-picture; fullscreen'
          allowFullScreen
        />
      ) : (
        <button
          type='button'
          onClick={onPlay}
          aria-label={playLabel}
          className='group absolute inset-0 block overflow-hidden focus:outline-none'
        >
          <img
            src={`https://i.ytimg.com/vi/${videoId}/maxresdefault.jpg`}
            alt=''
            loading='lazy'
            decoding='async'
            className='h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.03]'
          />
          <span className='absolute inset-0 bg-gradient-to-t from-black/55 via-black/5 to-black/15' />
          <span className='absolute inset-0 flex items-center justify-center'>
            <span className='flex h-16 w-16 items-center justify-center rounded-full bg-white/95 text-slate-900 shadow-2xl ring-8 ring-white/20 transition-transform duration-300 ease-out group-hover:scale-110 group-focus-visible:ring-primary-400 sm:h-20 sm:w-20'>
              <PlayGlyph className='ml-1 h-7 w-7 sm:h-8 sm:w-8' />
            </span>
          </span>
          {length && (
            <span className='absolute bottom-3 right-3 rounded-md bg-black/70 px-1.5 py-0.5 text-xs font-medium tabular-nums text-white'>
              {length}
            </span>
          )}
        </button>
      )}
    </div>
  );
}
